import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      component (cookie consent banner: accept-all/reject-all/custom)
 *   extends:   Component (@glimmer/component)
 *   template:  cookie-banner.hbs
 *   args:      -
 *   uses:      localStorage ('cookie-consent-v2'), global `gtag`, `window.dataLayer`
 *   siblings:  -
 *   tests:     cookie-banner-gtag-test.js, ui-components-test.js, wave17-components-test.js, list-components-test.js
 *   gotchas:   `gtag` is guarded (`typeof gtag !== 'function'`) in
 *              updateConsentState — it is legitimately absent whenever the
 *              analytics snippet is blocked/stripped, which is exactly the
 *              case this component exists for; calling it unguarded threw and
 *              skipped `showBanner = false` in every caller, making the banner
 *              undismissable for users who reject tracking. Same guard as
 *              beebee-base/src/lib/router-tracking.js:59.
 *
 * Persists cookie preferences (analytics/ads/personalization) to localStorage
 * and syncs Google's consent-mode state via gtag.
 */
export default class CookieBannerComponent extends Component {
  @tracked showBanner = false;
  @tracked customizing = false;

  @tracked analytics = false;
  @tracked ads = false;
  @tracked personalization = false;

  constructor() {
    super(...arguments);
    this.loadPreferences();
  }

  loadPreferences() {
    try {
      const saved = localStorage.getItem('cookie-consent-v2');
      if (!saved) {
        this.showBanner = true;
        return;
      }

      const prefs = JSON.parse(saved);

      // Applica preferenze locali
      this.analytics = prefs.analytics;
      this.ads = prefs.ads;
      this.personalization = prefs.personalization;

      this.updateConsentState(prefs);
    } catch (e) {
      // localStorage non disponibile (es. navigazione privata Safari, iframe cross-origin)
      this.showBanner = true;
    }
  }

  savePreferences(prefs) {
    try {
      localStorage.setItem('cookie-consent-v2', JSON.stringify(prefs));
    } catch (e) {
      // localStorage not available
    }
    this.analytics = prefs.analytics;
    this.ads = prefs.ads;
    this.personalization = prefs.personalization;
    this.updateConsentState(prefs);
  }

  updateConsentState(prefs) {
    // `gtag` is absent whenever the analytics snippet is blocked or stripped — which
    // is precisely the situation this component exists for. Calling it unguarded threw
    // a ReferenceError that propagated out of savePreferences() and skipped the
    // `showBanner = false` in every caller, so the banner became undismissable for
    // exactly the users who reject tracking; loadPreferences() hit the same throw and
    // its catch re-showed the banner, re-prompting consented visitors on every page
    // load. Same guard as beebee-base/src/lib/router-tracking.js:59. With no analytics
    // loaded there is no consent state to update, so skipping the call is correct —
    // the preference itself is already persisted by the caller.
    if (typeof gtag !== 'function') {
      return;
    }

    // Funzione unificata per aggiornare lo stato di consenso di Google
    gtag('consent', 'update', {
      ad_storage: prefs.ads ? 'granted' : 'denied',
      ad_user_data: prefs.ads ? 'granted' : 'denied',
      ad_personalization: prefs.personalization ? 'granted' : 'denied',
      analytics_storage: prefs.analytics ? 'granted' : 'denied',
    });
  }

  // --- Azioni ---
  @action
  acceptAll() {
    const prefs = {
      analytics: true,
      ads: true,
      personalization: true,
    };

    this.savePreferences(prefs);
    this.showBanner = false;
  }

  @action
  rejectAll() {
    const prefs = {
      analytics: false,
      ads: false,
      personalization: false,
    };

    this.savePreferences(prefs);
    this.showBanner = false;
  }

  @action
  saveCustom() {
    const prefs = {
      analytics: this.analytics,
      ads: this.ads,
      personalization: this.personalization,
    };

    this.savePreferences(prefs);
    this.showBanner = false;
  }
}
